import React, { useEffect, useMemo, useState } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { SubHeading } from 'components/Typography/General';

const MIN_LIMIT = 0;
const MAX_LIMIT = 15000; // Updated to 15000
// const MIN_GAP = 1000;

const sanitizeNumericValue = (value, fallback) => {
  const parsedValue = Number(String(value ?? '').replace(/[^0-9]/g, ''));
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const clampValue = (value, minValue, maxValue) => {
  return Math.min(Math.max(value, minValue), maxValue);
};

const getPriceBounds = (priceOptions, fallbackMax) => {
  if (!Array.isArray(priceOptions) || priceOptions.length === 0) {
    return {
      min: MIN_LIMIT,
      max: Math.min(Math.max(fallbackMax, MIN_LIMIT), MAX_LIMIT),
    };
  }

  const bounds = priceOptions.reduce(
    (accumulator, item) => {
      const nextMin = sanitizeNumericValue(item?.min, Number.POSITIVE_INFINITY);
      const nextMax = sanitizeNumericValue(item?.max, Number.NEGATIVE_INFINITY);

      return {
        min: Number.isFinite(nextMin) ? Math.min(accumulator.min, nextMin) : accumulator.min,
        max: Number.isFinite(nextMax) ? Math.max(accumulator.max, nextMax) : accumulator.max,
      };
    },
    {
      min: Number.POSITIVE_INFINITY,
      max: Number.NEGATIVE_INFINITY,
    }
  );

  return {
    min: Number.isFinite(bounds.min) ? bounds.min : MIN_LIMIT,
    // Ensure the max bound never exceeds 15000
    max: Math.min(
      Number.isFinite(bounds.max) ? bounds.max : Math.max(fallbackMax, MIN_LIMIT),
      MAX_LIMIT
    ),
  };
};

const FilterPrice = (props) => {
  const {
    defaultMaxPrice,
    fullWidth,
    maxPrice,
    minPrice,
    price,
    priceFilter,
    setMaxPrice,
    setMinPrice,
  } = props;
  
  const [show, setShow] = useState(true);
  const [activeThumb, setActiveThumb] = useState('max');
  
  const derivedPriceBounds = useMemo(
    () => getPriceBounds(price, sanitizeNumericValue(defaultMaxPrice, MAX_LIMIT)),
    [defaultMaxPrice, price]
  );
  
  const hasActivePriceFilter = Boolean(priceFilter);

  useEffect(() => {
    if (hasActivePriceFilter) return;

    const currentMinPrice = sanitizeNumericValue(minPrice, MIN_LIMIT);
    // Use the derived max (which is now capped at 15000)
    const currentMaxPrice = sanitizeNumericValue(maxPrice, derivedPriceBounds.max);
    const safeDefaultMaxPrice = sanitizeNumericValue(defaultMaxPrice, derivedPriceBounds.max);

    if (currentMinPrice === MIN_LIMIT && currentMinPrice !== derivedPriceBounds.min) {
      setMinPrice(derivedPriceBounds.min);
    }

    if (currentMaxPrice === safeDefaultMaxPrice && currentMaxPrice !== derivedPriceBounds.max) {
      setMaxPrice(derivedPriceBounds.max);
    }
  }, [
    defaultMaxPrice,
    derivedPriceBounds.max,
    derivedPriceBounds.min,
    hasActivePriceFilter,
    maxPrice,
    minPrice,
    setMaxPrice,
    setMinPrice,
  ]);

  // Constrain the logic to the 15000 limit
  const rawMinPrice = Math.max(sanitizeNumericValue(minPrice, MIN_LIMIT), MIN_LIMIT);
  const rawMaxPrice = clampValue(
    sanitizeNumericValue(maxPrice, MAX_LIMIT), 
    rawMinPrice, 
    MAX_LIMIT
  );
  
  const sliderMax = MAX_LIMIT; // Fixed the slider ceiling to 15000

  const safeMinPrice = clampValue(rawMinPrice, MIN_LIMIT, sliderMax);
  const safeMaxPrice = clampValue(rawMaxPrice, safeMinPrice, sliderMax);

  const minPos = (safeMinPrice / sliderMax) * 100;
  const maxPos = (safeMaxPrice / sliderMax) * 100;
  const areThumbsClose = maxPos - minPos <= 8;

  const handleMinChange = (e) => {
    setActiveThumb('min');
    const value = clampValue(
      sanitizeNumericValue(e.target.value, MIN_LIMIT),
      MIN_LIMIT,
      safeMaxPrice
    );
    setMinPrice(value);
  };

  const handleMaxChange = (e) => {
    setActiveThumb('max');
    const value = clampValue(
      sanitizeNumericValue(e.target.value, MAX_LIMIT),
      safeMinPrice,
      MAX_LIMIT
    );
    setMaxPrice(value);
  };

  return (
    <div className={`filter-box border ${fullWidth ? '' : 'max-width-297'}  mt-4`}>
     <SubHeading variety={5}>
        {props?.title}{' '}
        <i
          className={`bi p-2 ${show ? 'bi-chevron-up' : 'bi-chevron-down'} display-right font-size-12 cursor-pointer`}
          onClick={() => setShow(!show)}
        />
      </SubHeading>
    {show ? (
      <div>
        <div className="position-relative mb-5 mt-5" style={{ height: '15px', width: '90%' }}>
          <div 
            className="position-absolute w-100 rounded" 
            style={{ height: '6px', backgroundColor: '#E2E8F0', top: '9px' }} 
          />
          <div 
            className="position-absolute rounded" 
            style={{ 
              height: '6px', 
              backgroundColor: '#3182CE', 
              top: '9px',
              left: `${minPos}%`,
              right: `${100 - maxPos}%`
            }} 
          />

          <input
            type="range"
            min={MIN_LIMIT}
            max={sliderMax}
            value={safeMinPrice}
            onChange={handleMinChange}
            onFocus={() => setActiveThumb('min')}
            onMouseDown={() => setActiveThumb('min')}
            className="custom-range-slider"
            style={{ zIndex: activeThumb === 'min' || areThumbsClose ? 6 : 3 }}
          />
          <input
            type="range"
            min={MIN_LIMIT}
            max={sliderMax}
            value={safeMaxPrice ? safeMaxPrice : 1000}
            onChange={handleMaxChange}
            onFocus={() => setActiveThumb('max')}
            onMouseDown={() => setActiveThumb('max')}
            className="custom-range-slider"
            style={{ zIndex: activeThumb === 'max' ? 6 : 4 }}
          />
        </div>

        <Row className="align-items-end g-3" style={{ width: '90%' }}>
          <Col>
            <Form.Group>
              <Form.Label className="font-weight-600 font-size-14 mb-2 text-dark">Min Price</Form.Label>
              <Form.Control
                type="text"
                value={safeMinPrice}
                onChange={handleMinChange}
                className="form-control font-size-14"
                style={{height: '40px'}}
              />
            </Form.Group>
          </Col>
          <Col xs="auto" className="pb-2 text-secondary fw-bold"><div className="fw-bold" style={{marginTop: '-25px'}}>—</div></Col>
          <Col>
            <Form.Group>
              <Form.Label className="font-weight-600 font-size-14 mb-2 text-dark">Max Price</Form.Label>
              <Form.Control
                type="text"
                value={safeMaxPrice ? safeMaxPrice : 1000}
                onChange={handleMaxChange}
                className="form-control font-size-14"
                style={{height: '40px'}}
              />
            </Form.Group>
          </Col>
        </Row>
        <style>{`
          .custom-range-slider {
            position: absolute;
            width: 100%;
            pointer-events: none;
            appearance: none;
            background: transparent;
            top: 5px;
            margin: 0;
          }
          .custom-range-slider::-webkit-slider-thumb {
            height: 16px;
            width: 16px;
            border-radius: 50%;
            background: rgba(38, 133, 202, 1);
            pointer-events: auto;
            appearance: none;
            cursor: pointer;
          }
          .custom-range-slider::-moz-range-thumb {
            height: 16px;
            width: 16px;
            border-radius: 50%;
            background: rgba(38, 133, 202, 1);
            pointer-events: auto;
            cursor: pointer;
          }
          .custom-range-slider::-webkit-slider-runnable-track { background: transparent; }
          .custom-range-slider::-moz-range-track { background: transparent; }
        `}</style>
      </div>
      ) : null}
    </div>
  );
};

export default FilterPrice;